import { StockDispatchType } from '@/types';
import { createApi } from '@reduxjs/toolkit/query/react';
import { keepUnusedDataFor } from '../constants/appActions';
import { baseQueryWithReauth } from '../utils/prepHeaders';
import { inventoryApi } from './inventoryApi';
import { receivingApi } from './receivingApi';

type DispatchScopeQuery = {
  scope: StockDispatchType.DispatchScope;
  project_id?: number;
};

export type args = {
  getStockOptions: { query: DispatchScopeQuery & { search?: string } };
  getDispatches: {
    query: DispatchScopeQuery & {
      status?: StockDispatchType.DispatchStatus;
      search?: string;
    };
  };
  getDispatch: { params: { id: number } };
  postDispatch: { body: StockDispatchType.DispatchDraftInput };
  patchDispatch: {
    params: { id: number };
    body: StockDispatchType.DispatchDraftInput;
  };
  deleteDispatch: { params: { id: number } };
  postDispatchAction: { params: { id: number } };
  cancelDispatch: { params: { id: number } };
  voidDispatch: { params: { id: number }; body: { void_reason: string } };
};

export type responses = {
  getStockOptions: StockDispatchType.StockOption[];
  getDispatches: StockDispatchType.InventoryDispatch[];
  getDispatch: StockDispatchType.InventoryDispatch;
  postDispatch: StockDispatchType.InventoryDispatch;
  patchDispatch: StockDispatchType.InventoryDispatch;
  deleteDispatch: void;
  postDispatchAction: StockDispatchType.InventoryDispatch;
  cancelDispatch: StockDispatchType.InventoryDispatch;
  voidDispatch: StockDispatchType.InventoryDispatch;
};

const refreshInventoryAfter = async (
  queryFulfilled: Promise<unknown>,
  dispatch: (action: unknown) => unknown,
) => {
  try {
    await queryFulfilled;
    dispatch(
      receivingApi.util.invalidateTags([{ type: 'Receiving', id: 'items' }]),
    );
    dispatch(
      inventoryApi.util.invalidateTags([
        { type: 'Inventory', id: 'confirmed-receipts' },
        { type: 'Inventory', id: 'dashboard' },
      ]),
    );
  } catch {
    // Global API middleware reports the request error.
  }
};

const dispatchTags = [
  { type: 'StockDispatch' as const, id: 'list' },
  { type: 'StockDispatch' as const, id: 'stock-options' },
];

export const stockDispatchApi = createApi({
  reducerPath: 'stockDispatchApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['StockDispatch'],
  endpoints: (builder) => ({
    getStockOptions: builder.query<
      responses['getStockOptions'],
      args['getStockOptions']
    >({
      query: ({ query }) => ({
        url: '/inventory-dispatch/stock-options/',
        method: 'GET',
        params: query,
      }),
      keepUnusedDataFor,
      providesTags: [{ type: 'StockDispatch', id: 'stock-options' }],
    }),

    getDispatches: builder.query<
      responses['getDispatches'],
      args['getDispatches']
    >({
      query: ({ query }) => ({
        url: '/inventory-dispatch/dispatches/',
        method: 'GET',
        params: query,
      }),
      keepUnusedDataFor,
      providesTags: (result) => [
        { type: 'StockDispatch', id: 'list' },
        ...(result ?? []).map((dispatch) => ({
          type: 'StockDispatch' as const,
          id: dispatch.id,
        })),
      ],
    }),

    getDispatch: builder.query<responses['getDispatch'], args['getDispatch']>({
      query: ({ params }) => ({
        url: `/inventory-dispatch/dispatches/${params.id}/`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { params }) => [
        { type: 'StockDispatch', id: params.id },
      ],
    }),

    postDispatch: builder.mutation<
      responses['postDispatch'],
      args['postDispatch']
    >({
      query: ({ body }) => ({
        url: '/inventory-dispatch/dispatches/',
        method: 'POST',
        body,
      }),
      invalidatesTags: dispatchTags,
      onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        void refreshInventoryAfter(queryFulfilled, dispatch);
      },
    }),

    patchDispatch: builder.mutation<
      responses['patchDispatch'],
      args['patchDispatch']
    >({
      query: ({ params, body }) => ({
        url: `/inventory-dispatch/dispatches/${params.id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { params }) => [
        ...dispatchTags,
        { type: 'StockDispatch', id: params.id },
      ],
      onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        void refreshInventoryAfter(queryFulfilled, dispatch);
      },
    }),

    deleteDispatch: builder.mutation<
      responses['deleteDispatch'],
      args['deleteDispatch']
    >({
      query: ({ params }) => ({
        url: `/inventory-dispatch/dispatches/${params.id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: dispatchTags,
      onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        void refreshInventoryAfter(queryFulfilled, dispatch);
      },
    }),

    postDispatchAction: builder.mutation<
      responses['postDispatchAction'],
      args['postDispatchAction']
    >({
      query: ({ params }) => ({
        url: `/inventory-dispatch/dispatches/${params.id}/post/`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (_result, _error, { params }) => [
        ...dispatchTags,
        { type: 'StockDispatch', id: params.id },
      ],
      onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        void refreshInventoryAfter(queryFulfilled, dispatch);
      },
    }),

    cancelDispatch: builder.mutation<
      responses['cancelDispatch'],
      args['cancelDispatch']
    >({
      query: ({ params }) => ({
        url: `/inventory-dispatch/dispatches/${params.id}/cancel/`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (_result, _error, { params }) => [
        ...dispatchTags,
        { type: 'StockDispatch', id: params.id },
      ],
      onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        void refreshInventoryAfter(queryFulfilled, dispatch);
      },
    }),

    voidDispatch: builder.mutation<
      responses['voidDispatch'],
      args['voidDispatch']
    >({
      query: ({ params, body }) => ({
        url: `/inventory-dispatch/dispatches/${params.id}/void/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { params }) => [
        ...dispatchTags,
        { type: 'StockDispatch', id: params.id },
      ],
      onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        void refreshInventoryAfter(queryFulfilled, dispatch);
      },
    }),
  }),
});

export namespace StockDispatchApi {
  export const Api = stockDispatchApi;
  export type Args = args;
  export type Responses = responses;
}
